import { logger } from '@clarix/utils';
import { LiveInput, openMic, type OpenedMic } from './mic-input';
import type { CaptureFailure, MicChoice } from './types';
import { AUDIO_CONTEXT_RESUME_TIMEOUT_MS, DEAD_MIC_PEAK, RECORDER_STALL_MS, RECORDER_TICK_GAP_MS } from './constants';

/**
 * Hybrid AudioWorklet + MediaRecorder recorder.
 *
 * Architecture:
 *   Mic (LiveInput, swappable) → AudioWorkletNode (audio OS thread, never throttled)
 *                              ↓
 *                    MediaStreamDestination
 *                              ↓
 *                    MediaRecorder (encodes to audio/mp4 natively)
 *                              ↓
 *                    onChunk(Blob) callback
 *
 * Why hybrid?
 * - AudioWorklet runs in the browser's audio rendering thread — Chrome never
 *   suspends it when the tab is hidden. This guarantees continuous audio capture
 *   and prevents the mic stream from being throttled or dropped.
 * - MediaRecorder handles encoding to audio/mp4 natively — no external WASM
 *   encoder needed, and the output format is what the backend accepts.
 * - MediaRecorder's `ondataavailable` IS subject to tab throttling, but we
 *   mitigate this by calling `requestData()` when the tab hides (flush before
 *   freeze) and again when it becomes visible (collect buffered audio).
 *   The audio data is NEVER lost — MediaRecorder buffers it internally; we just
 *   get delayed delivery when the tab is inactive.
 *
 * The onChunk callback receives audio/mp4 Blobs ready for S3 upload.
 */

const MEDIA_RECORDER_INTERVAL_MS = 5000; // request a chunk every 5 s

/** A recorder that errored or was torn down may never answer; don't hang the stop on it. */
const RECORDER_REPLY_TIMEOUT_MS = 3000;

function awaitRecorder(
  recorder: MediaRecorder,
  event: 'dataavailable' | 'stop',
  trigger: () => void,
): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      recorder.removeEventListener(event, done);
      resolve();
    };
    const timer = setTimeout(done, RECORDER_REPLY_TIMEOUT_MS);
    recorder.addEventListener(event, done);
    trigger();
  });
}

/**
 * Stop `recorder` (recording or paused) and resolve once its final `dataavailable` has been
 * handled — the spec fires it just before `stop`. MP4's requestData() only returns finished
 * fragments, so this is the only way to get the tail of the take.
 */
export function stopMediaRecorder(recorder: MediaRecorder): Promise<void> {
  if (recorder.state === 'inactive') return Promise.resolve();
  return awaitRecorder(recorder, 'stop', () => recorder.stop());
}

/**
 * Calls `onStall` once when a visible, recording `recorder` hands over no audio, or `inputDead`
 * stays true, for RECORDER_STALL_MS. A late tick means the page was frozen (sleep), so both clocks
 * restart; a hidden tab's delivery is throttled, so they restart once it is seen again.
 */
export function watchRecorderData(
  recorder: MediaRecorder,
  onStall: () => void,
  inputDead: () => boolean = () => false,
): () => void {
  let lastAt = Date.now();
  let lastTick = lastAt;
  let deadSince: number | null = null;
  const fresh = () => {
    lastAt = Date.now();
    deadSince = null;
  };
  const onData = (event: BlobEvent) => {
    if (event.data.size > 0) lastAt = Date.now();
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') fresh();
  };
  const timer = window.setInterval(() => {
    const now = Date.now();
    const frozen = now - lastTick > RECORDER_TICK_GAP_MS;
    lastTick = now;
    if (frozen) fresh();
    if (recorder.state !== 'recording' || document.visibilityState !== 'visible') {
      deadSince = null;
      return;
    }
    deadSince = inputDead() ? (deadSince ?? now) : null;
    const silentFor = Math.max(now - lastAt, deadSince == null ? 0 : now - deadSince);
    if (silentFor < RECORDER_STALL_MS) return;
    dispose();
    onStall();
  }, 1000);
  function dispose() {
    window.clearInterval(timer);
    recorder.removeEventListener('dataavailable', onData);
    recorder.removeEventListener('start', fresh);
    recorder.removeEventListener('resume', fresh);
    recorder.removeEventListener('stop', dispose);
    document.removeEventListener('visibilitychange', onVisible);
  }
  recorder.addEventListener('dataavailable', onData);
  recorder.addEventListener('start', fresh);
  recorder.addEventListener('resume', fresh);
  recorder.addEventListener('stop', dispose);
  document.addEventListener('visibilitychange', onVisible);
  return dispose;
}

export interface DeadMicDetector {
  /** One PCM chunk from the worklet, in order. */
  add(pcm: Float32Array): void;
  /** Capture paused or resumed: the silence clock starts again. */
  reset(): void;
}

/**
 * Calls `onDead` once after RECORDER_STALL_MS of continuous digital silence (every sample under
 * DEAD_MIC_PEAK): a dead, muted or missing mic. It counts audio time from the samples themselves,
 * so it needs no timer and works the same in a hidden tab; a sleeping computer sends no samples.
 */
export function createDeadMicDetector(sampleRate: number, onDead: () => void): DeadMicDetector {
  const limit = Math.round((sampleRate * RECORDER_STALL_MS) / 1000);
  let silent = 0;
  let fired = false;
  return {
    add(pcm) {
      if (fired) return;
      let i = pcm.length;
      while (i > 0 && Math.abs(pcm[i - 1]!) < DEAD_MIC_PEAK) i--;
      // A sound restarts the count at the silence that follows it.
      silent = i === 0 ? silent + pcm.length : pcm.length - i;
      if (silent < limit) return;
      fired = true;
      onDead();
    },
    reset() {
      silent = 0;
    },
  };
}

/**
 * Feeds the worklet's PCM messages (`{ type: 'chunk', pcm }`, about every 5 s of audio) into a
 * dead-mic detector. Port messages are not throttled like timers, so this runs while hidden.
 */
export function listenForDeadMic(port: MessagePort, sampleRate: number, onDead: () => void): DeadMicDetector {
  const detector = createDeadMicDetector(sampleRate, onDead);
  port.onmessage = (event: MessageEvent<{ type?: string; pcm?: unknown }>) => {
    if (event.data?.type === 'chunk' && event.data.pcm instanceof Float32Array) detector.add(event.data.pcm);
  };
  return detector;
}

function getSupportedMimeType(): string {
  // Prefer AAC-in-MP4 (.m4a compatible, Sarvam STT can decode).
  // 'audio/mp4' without explicit codec lets Chrome pick Opus-in-MP4 which is
  // non-standard and breaks Sarvam transcription — always request AAC explicitly.
  const candidates = [
    'audio/mp4;codecs=aac',
    'audio/mp4;codecs=mp4a.40.2', // AAC-LC formal codec string
    'audio/mp4', // fallback — may be Opus-in-MP4 on Chrome
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
  ];
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return '';
}

/** Resolves once `context` runs or the wait times out; a context that never runs is caught as a stall. */
export async function resumeContext(context: AudioContext): Promise<void> {
  if (context.state === 'running') return;
  await Promise.race([
    context.resume().catch(() => undefined),
    new Promise((r) => setTimeout(r, AUDIO_CONTEXT_RESUME_TIMEOUT_MS)),
  ]);
}

export class AudioWorkletRecorder {
  private audioContext: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private input: LiveInput | null = null;
  private destinationNode: MediaStreamAudioDestinationNode | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private onChunkCallback: ((blob: Blob) => void) | null = null;
  private mimeType = '';
  private deadMic: DeadMicDetector | null = null;

  static isSupported(): boolean {
    return (
      typeof AudioContext !== 'undefined' &&
      typeof AudioWorklet !== 'undefined' &&
      typeof MediaRecorder !== 'undefined' &&
      typeof navigator?.mediaDevices?.getUserMedia === 'function'
    );
  }

  /**
   * Start recording.
   * @param onChunk  Called with each encoded audio/mp4 Blob (every ~5 s, or on flush).
   * @param onInputEnded  The live mic went away (unplugged); the caller decides what replaces it.
   */
  async start(
    onChunk: (blob: Blob) => void,
    {
      mic,
      onInputEnded,
      onError,
    }: { mic?: MicChoice | null; onInputEnded: () => void; onError?: (reason: CaptureFailure) => void },
  ): Promise<OpenedMic> {
    this.onChunkCallback = onChunk;

    // 1. Request microphone
    const opened = await openMic(mic, { fallback: true });

    // 2. The device's own rate: a forced rate that differs from the mic's makes Firefox and
    //    older Safari refuse createMediaStreamSource, and a later mic swap may change it.
    try {
      this.audioContext = new AudioContext();
      await resumeContext(this.audioContext);

      // 3. Load AudioWorklet processor (audio OS thread)
      await this.audioContext.audioWorklet.addModule('/worklets/recorder-processor.js');

      // 4. Create the WorkletNode — runs in the audio rendering thread
      this.workletNode = new AudioWorkletNode(this.audioContext, 'recorder-processor');

      // 5. Mono destination — the worklet outputs audio into it
      this.destinationNode = this.audioContext.createMediaStreamDestination();
      this.destinationNode.channelCount = 1;
      this.destinationNode.channelCountMode = 'explicit';

      // 6. Connect pipeline: mic → worklet → destination (no speaker output = no feedback)
      this.input = new LiveInput(this.audioContext, this.workletNode, onInputEnded);
      await this.input.attach(opened);
    } catch (err) {
      opened.stream.getTracks().forEach((t) => t.stop());
      throw err;
    }
    this.workletNode.connect(this.destinationNode);

    // 7. Create MediaRecorder on the worklet's output stream
    this.mimeType = getSupportedMimeType();
    if (!this.mimeType) throw new Error('No supported audio format found in your browser');

    this.mediaRecorder = new MediaRecorder(this.destinationNode.stream, {
      mimeType: this.mimeType,
    });

    // Set before any awaitRecorder listener, so a chunk is handed over before its waiter resolves.
    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.onChunkCallback?.(event.data);
    };

    this.mediaRecorder.onerror = (event) => {
      logger.error('[Recording/Worklet] MediaRecorder error:', event);
      onError?.('error');
    };
    if (onError) {
      this.deadMic = listenForDeadMic(this.workletNode.port, this.audioContext.sampleRate, () => onError('stalled'));
      // A context that never runs, or a muted / ended mic, records nothing: the same stall. The
      // recorder's own data is throttled in a hidden tab, so this watch runs only while visible.
      watchRecorderData(
        this.mediaRecorder,
        () => onError('stalled'),
        () => this.audioContext?.state !== 'running' || !this.input?.delivering,
      );
    }

    // 8. Handle AudioContext interruptions (iOS phone call, Siri, etc.)
    this.audioContext.onstatechange = () => {
      // A paused take stays paused: resuming the worklet here would let the dead-mic check stop it.
      if (this.audioContext?.state === 'running' && this.mediaRecorder?.state !== 'paused') {
        this.workletNode?.port.postMessage({ type: 'resume' });
      }
    };

    // 9. Tab visibility: flush before hide, collect on show
    this._handleVisibilityChange = this._handleVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this._handleVisibilityChange);

    // 10. Start MediaRecorder
    this.mediaRecorder.start(MEDIA_RECORDER_INTERVAL_MS);
    return opened;
  }

  /** The swappable mic side of the graph, with its meter tap; null before start / after destroy. */
  getInput(): LiveInput | null {
    return this.input;
  }

  private _handleVisibilityChange(): void {
    const mr = this.mediaRecorder;
    if (!mr || mr.state === 'inactive') return;

    if (document.hidden) {
      // Tab is hiding — request current data before Chrome throttles callbacks
      mr.requestData();
    } else {
      // Tab is visible again — collect any audio buffered while hidden
      mr.requestData();
    }
  }

  pause(): void {
    if (this.mediaRecorder?.state === 'recording') {
      this.mediaRecorder.requestData(); // flush before pause
      this.mediaRecorder.pause();
      this.workletNode?.port.postMessage({ type: 'pause' });
      this.deadMic?.reset();
    }
  }

  resume(): void {
    if (this.audioContext?.state === 'suspended') {
      void this.audioContext.resume();
    }
    if (this.mediaRecorder?.state === 'paused') {
      this.mediaRecorder.resume();
      this.workletNode?.port.postMessage({ type: 'resume' });
      this.deadMic?.reset();
    }
  }

  /**
   * Hand over what has been encoded so far. Not a substitute for stop(): MP4 answers only up to
   * its last finished fragment, and an empty reply still resolves.
   */
  flush(): Promise<void> {
    const mr = this.mediaRecorder;
    if (!mr || mr.state === 'inactive') return Promise.resolve();
    return awaitRecorder(mr, 'dataavailable', () => mr.requestData());
  }

  /** Stop encoding and wait until the last chunk has reached onChunk; call before destroy(). */
  stop(): Promise<void> {
    return this.mediaRecorder ? stopMediaRecorder(this.mediaRecorder) : Promise.resolve();
  }

  /** The MIME type actually used by the underlying MediaRecorder. */
  getMimeType(): string {
    return this.mimeType;
  }

  destroy(): void {
    document.removeEventListener('visibilitychange', this._handleVisibilityChange);

    // Discards whatever is still encoding (abort); a pending flush/stop resolves on its events.
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    this.mediaRecorder = null;

    this.input?.stop();
    if (this.workletNode) this.workletNode.port.onmessage = null;
    this.deadMic = null;
    this.workletNode?.disconnect();
    void this.audioContext?.close();

    this.input = null;
    this.workletNode = null;
    this.destinationNode = null;
    this.audioContext = null;
    this.onChunkCallback = null;
  }
}
